import React, { useMemo } from "react";
import remarkGfm from "remark-gfm";
import { Streamdown } from "streamdown";
import { code } from "@streamdown/code";

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /**
   * Use streaming-optimized parsing + smoother incomplete markdown (live assistant).
   * Completed thread messages should use static mode (default).
   */
  isStreaming?: boolean;
  /** While tokens are arriving; keeps Streamdown animation/caret behavior in sync. */
  isAnimating?: boolean;
}

/**
 * Allowlist safe URL schemes for links rendered from model/citation output.
 * Blocks `javascript:`, `data:`, `vbscript:`, etc. Relative links and anchors
 * (`/path`, `#id`) and `http(s)`/`mailto` are kept; anything else is dropped.
 */
function sanitizeHref(href?: string): string | undefined {
  if (!href) return undefined;
  const value = href.trim();
  // Relative paths and in-page anchors are safe.
  if (value.startsWith("/") || value.startsWith("#")) return value;
  try {
    const scheme = new URL(value, window.location.origin).protocol.toLowerCase();
    if (scheme === "http:" || scheme === "https:" || scheme === "mailto:") {
      return value;
    }
  } catch {
    // Unparseable — treat as unsafe.
  }
  return undefined;
}

function createMarkdownComponents() {
  return {
    p: ({ children }: any) => (
      <p className="mb-3 last:mb-0 leading-[1.65] text-theme-fg">{children}</p>
    ),
    h1: ({ children }: any) => (
      <h1 className="mt-8 mb-3 text-xl font-semibold leading-snug tracking-tight text-theme-fg first:mt-0">
        {children}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="mt-7 mb-3 text-lg font-semibold leading-snug tracking-tight text-theme-fg first:mt-0">
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="mt-5 mb-2.5 text-base font-semibold leading-snug tracking-tight text-theme-fg first:mt-0">
        {children}
      </h3>
    ),
    strong: ({ children }: any) => (
      <strong className="font-semibold text-theme-fg">{children}</strong>
    ),
    ul: ({ children }: any) => (
      <ul className="mb-4 mt-1 list-outside list-disc space-y-2.5 pl-5 marker:text-theme-fg-muted">
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className="mb-4 mt-1 list-outside list-decimal space-y-2.5 pl-5 marker:font-medium marker:text-theme-fg-muted">
        {children}
      </ol>
    ),
    li: ({ children }: any) => (
      <li className="leading-[1.65] text-theme-fg [&>p]:mb-2 [&>p:last-child]:mb-0 [&_ul]:mt-2 [&_ol]:mt-2">
        {children}
      </li>
    ),
    a: ({ href, children }: any) => {
      const safeHref = sanitizeHref(href);
      const text = typeof children === "string" ? children : "";
      const isCitation = /^\d+$/.test(text.trim());
      if (isCitation) {
        return (
          <a
            href={safeHref}
            target="_blank"
            rel="noopener noreferrer"
            title={safeHref}
            className="inline-flex items-center rounded border border-theme-border bg-theme-surface-ui px-1 py-0 text-[10px] font-medium leading-4 text-theme-fg-muted no-underline transition-colors hover:border-theme-primary hover:text-theme-primary align-super"
          >
            {text}
          </a>
        );
      }
      return (
        <a
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-theme-primary underline decoration-theme-primary/35 underline-offset-[3px] transition-opacity hover:opacity-90"
        >
          {children}
        </a>
      );
    },
    blockquote: ({ children }: any) => (
      <blockquote className="my-4 border-l-[3px] border-l-theme-border py-0.5 pl-4 italic leading-[1.65] text-theme-fg-muted">
        {children}
      </blockquote>
    ),
    table: ({ children }: any) => (
      <div className="my-4 overflow-x-auto rounded-lg border border-theme-border">
        <table className="min-w-full border-collapse text-left text-sm">{children}</table>
      </div>
    ),
    th: ({ children }: any) => (
      <th className="border-b border-theme-border bg-theme-background px-3 py-2.5 font-semibold text-theme-fg">
        {children}
      </th>
    ),
    td: ({ children }: any) => (
      <td className="border-b border-theme-border px-3 py-2.5 text-theme-fg">{children}</td>
    ),
    hr: () => <hr className="my-6 border-0 border-t border-theme-border" />,
  };
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = "",
  isStreaming = false,
  isAnimating = false,
}) => {
  const components = useMemo(() => createMarkdownComponents(), []);

  const streamdown = (
    <Streamdown
      mode={isStreaming ? "streaming" : "static"}
      isAnimating={isStreaming && isAnimating}
      caret={isStreaming && isAnimating ? "block" : undefined}
      remarkPlugins={[remarkGfm]}
      plugins={{ code }}
      shikiTheme={["github-light", "github-dark"]}
      className={`streamdown-chat max-w-none text-[15px] leading-[1.65] text-theme-fg antialiased [&>*:first-child]:mt-0 ${className}`}
      components={components}
    >
      {content}
    </Streamdown>
  );

  // Leading-edge fade while streaming: new text emerges softly at the bottom
  // edge and sharpens as it scrolls up, giving a gentle "fade in" without any
  // per-word DOM churn. We deliberately avoid Streamdown's built-in `animated`
  // plugin here — it re-runs its char-count effect on our high-frequency reveal
  // repaints and trips React's "Maximum update depth exceeded" on long streams.
  // A pure CSS mask has no such cost and never re-renders. Off for finished
  // (static) messages so completed replies are fully crisp.
  if (isStreaming && isAnimating) {
    const edge =
      "linear-gradient(to bottom, #000 calc(100% - 1.15em), transparent 100%)";
    return (
      <div style={{ WebkitMaskImage: edge, maskImage: edge }}>{streamdown}</div>
    );
  }

  return streamdown;
};
